import { ChartLineUpIcon } from "@phosphor-icons/react/dist/csr/ChartLineUp";
import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { RUN_STATUS } from "@/profitability/constants";
import type { ProfitabilityRun } from "@/profitability/types";
import { useRerunReport } from "@/profitability/use-rerun-report";
import { runDates } from "@/profitability/utils";
import { RunStatePanel } from "./run-state-panel";
export const RunContent = ({
  businessId,
  run,
  children,
}: {
  businessId: string;
  run: ProfitabilityRun;
  children: ReactNode;
}) => {
  const { rerun, rerunning } = useRerunReport(businessId, run);
  if (run.status === RUN_STATUS.failed)
    return (
      <ErrorState
        retrying={rerunning}
        title="This report could not be built"
        description={
          run.error?.message ??
          "Something went wrong while building this report."
        }
      >
        {!run.draft_sample && (
          <Button
            onClick={() => rerun()}
            disabled={rerunning}
            loading={rerunning}
            loadingLabel={`${runDates(run) ? "Try these dates again" : "Try this report again"}, in progress`}
          >
            {runDates(run) ? "Try these dates again" : "Try this report again"}
          </Button>
        )}
        {run.draft_sample && (
          <span>Return to the agent to rebuild this draft.</span>
        )}
      </ErrorState>
    );

  if (run.status !== RUN_STATUS.completed)
    return (
      <RunStatePanel
        icon={<Spinner aria-hidden />}
        isStatus
        title={
          run.status === RUN_STATUS.pending
            ? "Your report is queued"
            : "Building your report"
        }
        body={
          "Volca is pulling this range from ServiceTitan and running the numbers. You can leave this page. The report keeps building."
        }
      />
    );

  if (!run.workbook_available)
    return (
      <RunStatePanel
        icon={<ChartLineUpIcon aria-hidden />}
        title="The results are no longer available"
        body="This report finished, but its workbook has since been removed. Building these dates again produces a fresh copy."
        action={
          !run.draft_sample && (
            <Button
              onClick={() => rerun()}
              disabled={rerunning}
              loading={rerunning}
              loadingLabel="Build it again, in progress"
            >
              Build it again
            </Button>
          )
        }
      />
    );

  return children;
};
